import {eventChannel} from 'redux-saga';
import {FULFILLED} from 'redux-promise-middleware';
import {SET_MARKETING_COUPON_EVENT} from '@optimaxdev/marketing';

import {takeLatest, call, takeEvery, select, all, put} from 'redux-saga/effects';
import {sessionStore} from 'libs/storage/storage';
import {MARKETING_API_PROMOCODE_KEY} from 'constants/product';
import {LOCATION_CHANGED, QUOTE_ITEM} from 'constants/actions';
import {QuoteItemType} from 'constants/flow/flowTypesCart';
import {getAddAndUpdateCartDataForPrice} from 'features/wizardMs/selectors/product/product';
import {getActivePackageForPriceEndPoint} from 'features/wizardMs/selectors/packages/packages';
import {isContactLenses as isContactLensesSelector} from 'selectors/product/product';
import {fetchDiscountPrice} from 'features/wizardMs/reducers/discountPrice/discountPrice';
import {getQuoteItems} from 'selectors/cart/cart';

/**
 * Create eventChannel to listen every call marketing coupon event
 * @return {function} channel creator
 */
export const createChannel = () =>
    eventChannel((emitter: (input: CustomEvent) => void): (() => void) => {
        const listener = (event: CustomEvent) => {
            emitter(event);
        };

        // @ts-ignore
        window.addEventListener(SET_MARKETING_COUPON_EVENT, listener);
        return () => {
            // @ts-ignore
            window.removeEventListener(SET_MARKETING_COUPON_EVENT, listener);
        };
    });

/**
 * It triggers to request new price when marketing api sets a new coupon
 */
export function* updatePrice() {
    const [glassesAddToCartData, glassesActivePackage, isContactLenses] = yield all([
        select(getAddAndUpdateCartDataForPrice),
        select(getActivePackageForPriceEndPoint),
        select(isContactLensesSelector),
    ]);
    // As I learnt - our marketing department likes to call marketingAPI.setCoupon() outside PDP/Configure pages
    // the request will be with api/rest/catalog/product/NaN/price?productId=NaN&coupon=CONTACTS25&quantity=1&version=v2.0
    // we should not allow to make an query for backend, but should save coupon to local storage. See setCoupon func
    const data = glassesAddToCartData;
    if (!data.productId || isContactLenses) return;

    yield put(
        fetchDiscountPrice({
            ...data,
            options: {
                ...glassesAddToCartData.options,
                [glassesActivePackage.parentNodeId]: glassesActivePackage.node,
            },
        }),
    );
}

/**
 * Sets coupon from call window.marketingAPI.setCoupon to session store
 * @param {CustomEvent} event - custom event, dispatched from console via gtm, DY, etc via window.marketingAPI.setCoupon
 */
export function* setCoupon({detail}: CustomEvent) {
    yield call([sessionStore, 'set'], MARKETING_API_PROMOCODE_KEY, detail);
    yield call(updatePrice);
}

/**
 * We should delete marketing's promocode when customer leaves PDP
 */
export function* resetCoupon() {
    yield call([sessionStore, 'set'], MARKETING_API_PROMOCODE_KEY, '');
}

/**
 * Set quote items in the marketing API
 */
export function* setQuotesData() {
    if (__NODEJS__) return;

    const cart = yield select(getQuoteItems);
    const itemsImgs: {imageUrl: string} = cart.map((item: QuoteItemType) => ({
        imageUrl: item.img,
    }));
    // @ts-ignore
    window.marketingAPI.setQuote(itemsImgs);
}

/**
 * The saga is relevant only for PDP
 */
export function* marketingAPI() {
    const channel = yield call(createChannel);
    yield takeLatest(channel, setCoupon);
    yield takeEvery(LOCATION_CHANGED, resetCoupon);
    yield takeEvery([`${QUOTE_ITEM.GET_ITEMS}_${FULFILLED}`], setQuotesData);
}
